import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Paper, Typography, Box, Button } from '@mui/material';
import { CustomSlider } from './CustomSlider';
import styles from './AdjustmentPanel.module.css';

const PANEL_WIDTH = 280;

export default function AdjustmentPanel({ camId, settings, onUpdate, onApplyToAll, onResetAll, onClose, flags, anchorRef }) {
    const panelRef = useRef(null);

    const [localValue, setLocalValue] = useState({
        brightness: settings?.brightness ? settings?.brightness : 0,
        contrast: settings?.contrast ? settings?.contrast : 0,
    });

    const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        if (anchorRef?.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            const spaceOnRight = window.innerWidth - rect.right;
            const left = spaceOnRight >= PANEL_WIDTH ? rect.right - PANEL_WIDTH : Math.max(4, rect.left - PANEL_WIDTH + rect.width);

            const panelHeight = panelRef.current?.offsetHeight ?? 0;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openAbove = flags?.openWindowVideoIdOutput || spaceBelow < panelHeight;
            const top = openAbove ? rect.top - panelHeight : rect.bottom;

            setPanelPosition({ top, left });
        }
    }, [anchorRef, flags?.openWindowVideoIdOutput]);

    const handleChange = (val, key) => {
        setLocalValue((prev) => ({
            ...prev,
            [key]: val,
        }));
        onUpdate(camId, key, val);
    };

    const handleResetAll = () => {
        setLocalValue({
            brightness: 0,
            contrast: 0,
        });
        onResetAll();
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (panelRef.current && !panelRef.current.contains(event.target) && !(anchorRef?.current && anchorRef.current.contains(event.target))) {
                onClose();
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, anchorRef]);

    useEffect(() => {
        function handleScroll() {
            onClose();
        }
        window.addEventListener('scroll', handleScroll, true);
        return () => window.removeEventListener('scroll', handleScroll, true);
    }, [onClose]);

    return createPortal(
        <Paper ref={panelRef} elevation={24} className={styles.panelContainer} style={{ top: panelPosition.top, left: panelPosition.left }}>
            <Box className={styles.panelHeader}>
                <Typography className={styles.panelHeaderText}>Temporary changes, only in AIQ.</Typography>
            </Box>

            <Box className={styles.panelBody}>
                <SliderRow
                    label="Brightness"
                    value={localValue.brightness}
                    onChange={(val) => handleChange(val, 'brightness')}
                    onReset={() => handleChange(0, 'brightness')}
                />
                <SliderRow
                    label="Contrast"
                    value={localValue.contrast}
                    onChange={(val) => handleChange(val, 'contrast')}
                    onReset={() => handleChange(0, 'contrast')}
                />
            </Box>

            <Box className={styles.panelFooter}>
                <Button onClick={onApplyToAll} disableRipple className={styles.btnApplyAll}>
                    Apply to all cameras
                </Button>
                <Button onClick={handleResetAll} disableRipple className={styles.btnReset}>
                    Reset all
                </Button>
            </Box>
        </Paper>,
        document.body
    );
}

function SliderRow({ label, value, onChange, onReset }) {
    return (
        <Box className={styles.sliderRowContainer}>
            <Box className={styles.sliderRowHeader}>
                <Typography className={styles.sliderLabel}>{label}</Typography>
                <Button onClick={onReset} disableRipple className={styles.btnRowReset}>
                    Reset
                </Button>
            </Box>

            <Box className={styles.sliderWrapper}>
                <CustomSlider
                    value={value}
                    min={-50}
                    max={50}
                    step={1}
                    marks={[{ value: 0 }]}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(val) => (val > 0 ? `+${val}%` : `${val}%`)}
                    onChange={(e, val) => onChange(val)}
                />

                <Box className={styles.sliderIndicators}>
                    <span>&minus;</span>
                    <span>&#x002B;</span>
                </Box>
            </Box>
        </Box>
    );
}
